export type PreviewType =
	| "image"
	| "video"
	| "pdf"
	| "docx"
	| "excel"
	| "csv"
	| "markdown"
	| "html"
	| "svg"
	| "other";

const EXT_MAP: Record<string, PreviewType> = {
	png: "image",
	jpg: "image",
	jpeg: "image",
	gif: "image",
	webp: "image",
	mp4: "video",
	webm: "video",
	mov: "video",
	pdf: "pdf",
	docx: "docx",
	xlsx: "excel",
	xlsm: "excel",
	xls: "excel",
	csv: "csv",
	tsv: "csv",
	md: "markdown",
	html: "html",
	htm: "html",
	svg: "svg",
};

/** バックエンド SandboxArchiveFormats と同じ対応形式。 */
const ARCHIVE_SUFFIXES = [".tar.gz", ".tgz", ".tar", ".zip", ".gz"] as const;

export function isArchiveFileName(fileName: string): boolean {
	const lower = fileName.toLowerCase();
	return ARCHIVE_SUFFIXES.some((suffix) => lower.length > suffix.length && lower.endsWith(suffix));
}

export type FileIconType =
	| "image"
	| "video"
	| "archive"
	| "json"
	| "markdown"
	| "python"
	| "javascript"
	| "typescript"
	| "shell"
	| "html"
	| "css"
	| "yaml"
	| "config"
	| "sql"
	| "csv"
	| "file";

/** プレビュー種別 (EXT_MAP) でアイコンが決まらない言語・形式の対応。seti-ui の対応に近い拡張子もまとめて寄せる。 */
const LANGUAGE_ICONS: Record<string, FileIconType> = {
	json: "json",
	py: "python",
	pyi: "python",
	js: "javascript",
	mjs: "javascript",
	cjs: "javascript",
	jsx: "javascript",
	ts: "typescript",
	tsx: "typescript",
	sh: "shell",
	bash: "shell",
	zsh: "shell",
	css: "css",
	scss: "css",
	less: "css",
	yaml: "yaml",
	yml: "yaml",
	toml: "config",
	ini: "config",
	cfg: "config",
	conf: "config",
	sql: "sql",
};

function extensionOf(filename: string): string {
	const dot = filename.lastIndexOf(".");
	return dot > 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

/** ファイルツリーなどで名前の横に出すアイコン種別を拡張子から決める。 */
export function getFileIconType(filename: string): FileIconType {
	if (isArchiveFileName(filename)) return "archive";
	const ext = extensionOf(filename);
	const preview = Object.hasOwn(EXT_MAP, ext) ? EXT_MAP[ext] : "other";
	switch (preview) {
		case "svg":
			return "image";
		case "image":
		case "video":
		case "markdown":
		case "html":
		case "csv":
			return preview;
	}
	if (Object.hasOwn(LANGUAGE_ICONS, ext)) return LANGUAGE_ICONS[ext];
	return "file";
}

/**
 * UTF-8 として厳密にデコードし、テキストとして扱えなければ null を返す。
 * NUL バイトを含む内容は UTF-8 として妥当でもバイナリとみなす。
 */
export function decodeUtf8(bytes: Uint8Array): string | null {
	if (bytes.includes(0)) return null;
	try {
		return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
	} catch {
		return null;
	}
}

/** 専用ビューアを持たないファイルは内容を確認して表示方法を決める。 */
export function getPreviewType(filename: string | null): PreviewType {
	if (!filename) return "other";
	const ext = extensionOf(filename);
	return Object.hasOwn(EXT_MAP, ext) ? EXT_MAP[ext] : "other";
}

/** テキストファイルのプレビュー・編集保存を許可する最大バイト数。バックエンドの保存上限と揃える。 */
export const MAX_EDITABLE_BYTES = 1_000_000;

/** .docx プレビューをクライアント側 (zip 展開 + DOM 構築) で描画してよい最大バイト数。 */
export const MAX_DOCX_PREVIEW_BYTES = 20_000_000;

/** Excel / CSV プレビューをクライアント側でパースしてよい最大バイト数。 */
export const MAX_TABLE_PREVIEW_BYTES = 20_000_000;
